import { createContext, PropsWithChildren, useRef, useState } from 'react';
import {
  UseMutateFunction,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import {
  initialQueryCards,
  registerFeedback,
  sessionQuery,
} from '@/queries/bot.queries';
import { EBotNamesBackend } from '@/types/botConfigTypes';
import {
  ECardTypes,
  // ECardTypes,
  // EEventTypes,
  IQuestionType,
  IResponseQuestionType,
} from '@/types';
import { streamChat } from '@/queries/chat.stream';
import parsePartialJson from '@/utils/parsePartialJson';
import uniqueId from 'lodash/uniqueId';
import {
  IPredefinedCardsResponseTypes,
  IRegisterFeedbackResponse,
} from '@/types/queryTypes';
import delay from '@/utils/delay';
import useAppContext from '@/hooks/useAppContext';
import { DEFAULT_GENDER, DEFAULT_DOB } from '@/constants/sessionDefaults';

interface ISessionProviderContext {
  sessionId?: string;
  onChange: (value: string) => void;
  handleOnSend: (searchValue?: string) => void;
  searchValue: string;
  questions: IResponseQuestionType[];
  onSubmitQuestion: (answer: string) => void;
  latestQuestion?: IResponseQuestionType;
  isLoading: boolean;
  isStreaming: boolean;
  predefinedCards?: IPredefinedCardsResponseTypes[];
  isPredefinedCardsLoading?: boolean;
  refreshState: () => void;
  registerFeedbackMutation?: UseMutateFunction<
    IRegisterFeedbackResponse | null,
    Error,
    {
      feedback: string;
      conversation_id: string;
    },
    unknown
  >;
  isClientError?: boolean;
  handleRetry?: () => void;
  feedbackData?: IRegisterFeedbackResponse | null;
  refreshStateWithOnSend?: (question: string) => Promise<void>;
  showDisclaimer?: boolean;
  setShowDisclaimer?: (value: boolean) => void;
  cancelInProgressRequests?: () => void;
}

export const SessionProviderContext =
  createContext<ISessionProviderContext | null>(null);

function SessionProvider({ children }: PropsWithChildren) {
  const { gender, dateOfBirth } = useAppContext();

  const botName = EBotNamesBackend.SPECIALITY_MATCH;
  const sessionQueryParams = useQuery({
    ...sessionQuery({
      context: {
        userDetails: {
          gender: gender ?? DEFAULT_GENDER,
          dateOfBirth: dateOfBirth ?? DEFAULT_DOB,
        },
      },
      botName,
    }),
    refetchInterval: 1000 * 60 * 60, // 60 minutes,
  });
  const { data: sessionData } = sessionQueryParams;
  const sessionId = sessionData?.sessionId;
  const [searchValue, setSearchValue] = useState('');
  const [questions, setQuestions] = useState<IResponseQuestionType[]>([]);
  const onChange = (value: string) => {
    setSearchValue(value);
  };
  const [latestQuestion, setLatestQuestion] = useState<IResponseQuestionType>();
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isClientError, setIsClientError] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [triggerErrorBoundary, setTriggerErrorBoundary] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const sessionIdRef = useRef<string | undefined>(sessionId);
  sessionIdRef.current = sessionId;

  const initialQuestions = useQuery({
    ...initialQueryCards(botName),
    staleTime: Infinity,
    retry: 1,
  });

  const { mutate: registerFeedbackMutation, data: feedbackData } = useMutation<
    IRegisterFeedbackResponse | null,
    Error,
    { feedback: string; conversation_id: string }
  >(registerFeedback({ botName, sessionId }));

  const streamAndHandleChatResponse = async (message: string) => {
    // Cancel any previous request
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    if (!sessionId && !sessionIdRef.current) {
      setTriggerErrorBoundary(true);
      return;
    }
    let accumulated: Pick<
      IQuestionType,
      'title' | 'subtitle' | 'cardType' | 'options'
    > = { cardType: ECardTypes.SINGLE_SELECT_TYPE };
    let conversation_id: string = '';
    try {
      await streamChat(
        {
          sessionId: sessionIdRef.current ? sessionIdRef.current : '',
          message,
          botName,
        },
        (chunk: string) => {
          try {
            const update = parsePartialJson(chunk ?? '');
            window.dev.i('Parsed chunk:', update);
            if (update) {
              accumulated = { ...accumulated, ...update }; // merge partials
              const tempUniqueId = uniqueId('conversation_id');
              setLatestQuestion({
                conversation_id: conversation_id ?? tempUniqueId,
                response: accumulated as IQuestionType,
              });
              setIsLoading(false);
              setIsStreaming(true);
            }
          } catch (e) {
            window.dev.e('Error parsing chunk:', e);
          }
        },
        (chunk) => {
          try {
            const match = chunk?.match(
              new RegExp(`"conversation_id"\\s*:\\s*"([^"]*)`),
            );
            if (match) {
              conversation_id = match[1];
            }
          } catch (e) {
            window.dev.e('Error parsing conversation_id:', e);
            conversation_id = uniqueId('conversation_id');
          }
          const update = parsePartialJson(chunk ?? '');
          accumulated = { ...accumulated, ...update };
          if (!conversation_id) {
            throw new Error('Conversation ID is not available');
          }
          setLatestQuestion({
            conversation_id: conversation_id ?? uniqueId(),
            response: accumulated as IQuestionType,
          });
          setIsStreaming(false);
          setIsLoading(false);
        },
        abortControllerRef.current.signal, // Pass abort signal to streamChat
      );
    } catch (error: unknown) {
      if (
        typeof error === 'object' &&
        error !== null &&
        'name' in error &&
        (error as { name?: string }).name === 'AbortError'
      ) {
        window.dev.i('Request aborted');
      } else {
        setIsClientError(true);
        window.dev.e('Error in streamAndHandleChatResponse:', error);
      }
      setIsLoading(false);
      setIsStreaming(false);
    }
  };
  // Call this when back is pressed to cancel in-progress API calls
  const cancelInProgressRequests = () => {
    abortControllerRef.current?.abort();
  };

  const handleOnSend = async (searchValueToNavigate?: string) => {
    if (searchValueToNavigate) {
      setSearchValue(searchValueToNavigate);
      setIsLoading(true);
      await streamAndHandleChatResponse(searchValueToNavigate);
      return;
    }
    if (searchValue.length === 0 || !sessionId) return;
    setIsLoading(true);
    await streamAndHandleChatResponse(searchValue);
  };
  const onSubmitQuestion = async (answer: string) => {
    if (!sessionId) return;
    /**
     * Have added a delay to ensure there's a transition before the api call
     * else, there's a flicker which causes UI to jump before the user sees
     * their own answer
     */
    await delay(250); // Delay for 1 second
    setIsLoading(true);
    const restQuestions = questions.filter(
      (question) =>
        question.conversation_id !== latestQuestion?.conversation_id,
    );
    const currentQuestionWithAnswer = {
      ...latestQuestion,
      answer,
    };
    setQuestions([
      ...restQuestions,
      currentQuestionWithAnswer as IResponseQuestionType,
    ]);

    await streamAndHandleChatResponse(answer);
  };

  /**
   * Refreshes the session state by canceling any in-progress requests,
   * resetting the questions, latest question, loading states, and client error state.
   * It also refetches the session query parameters and handles any errors by setting
   * a trigger for the error boundary.
   */

  const refreshState = async () => {
    // Cancel any in-progress requests before resetting state
    abortControllerRef.current?.abort();
    setQuestions([]);
    setLatestQuestion(undefined);
    setIsLoading(false);
    setIsStreaming(false);
    setIsClientError(false);
    setSearchValue('');
    setShowDisclaimer(true);
    const data = await sessionQueryParams.refetch();
    if (data.isError) {
      setTriggerErrorBoundary(true);
      return;
    }
    if (data.data?.sessionId) {
      sessionIdRef.current = data.data.sessionId;
    }
  };

  const refreshStateWithOnSend = async (question: string) => {
    setQuestions([]);
    setLatestQuestion(undefined);
    setIsLoading(false);
    setIsStreaming(false);
    setIsClientError(false);
    const newSessionId = await sessionQueryParams.refetch();
    if (newSessionId.isError) {
      setTriggerErrorBoundary(true);
      return;
    }
    if (newSessionId.data?.sessionId) {
      sessionIdRef.current = newSessionId.data.sessionId;
      await handleOnSend(question);
    }
  };

  const handleRetry = () => {
    /**
     * Re-establish the session
     */
    refreshState();
    setSearchValue('');
  };

  if (triggerErrorBoundary) {
    throw new Error('Failed to refresh session state');
  }

  return (
    <SessionProviderContext.Provider
      value={{
        sessionId,
        onChange,
        handleOnSend,
        searchValue,
        questions,
        onSubmitQuestion,
        latestQuestion: latestQuestion,
        isLoading,
        isStreaming,
        predefinedCards: initialQuestions.data?.cards,
        isPredefinedCardsLoading: initialQuestions.isLoading,
        refreshState,
        registerFeedbackMutation,
        isClientError,
        handleRetry,
        feedbackData,
        refreshStateWithOnSend,
        showDisclaimer,
        setShowDisclaimer,
        cancelInProgressRequests,
      }}
    >
      {children}
    </SessionProviderContext.Provider>
  );
}

export default SessionProvider;
